import { useState } from 'react';
import { AlertTriangle, Check, Undo2 } from 'lucide-react';
import { C, S, btn, clampLines } from './mailStyles';
import { resultText, safeExternalUrl } from './mailUtils';

function count(v) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

function StatTile({ label, value }) {
  return (
    <div style={{ flex: 1, minWidth: 0, background: C.bgMuted, borderRadius: 10, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ ...S.ellipsis, fontSize: 18, fontWeight: 700 }}>{value.toLocaleString()}</span>
      <span style={{ ...S.ellipsis, fontSize: 11.5, color: C.textDim }}>{label}</span>
    </div>
  );
}

function ManualRow({ name, url }) {
  const link = safeExternalUrl(url);
  return (
    <li
      data-testid="mail-manual-row"
      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 10, background: '#FFF4E5', minWidth: 0 }}
    >
      <AlertTriangle size={14} color={C.orange} style={{ flexShrink: 0 }} aria-hidden="true" />
      <span style={{ flex: 1, minWidth: 0, fontSize: 13, ...clampLines(2) }}>
        {name || 'This sender'} doesn't support one-tap unsubscribe
      </span>
      {link && (
        <a
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          title={link.domain}
          style={{ ...S.btnLink, textDecoration: 'none', flexShrink: 0, maxWidth: '45%', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}
        >
          <span>Open page ↗</span>
          <span style={{ ...S.ellipsis, maxWidth: '100%', fontSize: 11, fontWeight: 400, color: C.textFaint }}>{link.domain}</span>
        </a>
      )}
    </li>
  );
}

function UnsubscribeResult({ data, sendFollowUp }) {
  const [undoTapped, setUndoTapped] = useState(false);
  const unsubscribed = count(data?.unsubscribed);
  const archived = count(data?.archived);
  const manual = (Array.isArray(data?.manual) ? data.manual : [])
    .filter((m) => m && typeof m === 'object')
    .map((m) => ({ name: typeof m.name === 'string' ? m.name : '', url: m.url }));
  const failed = (Array.isArray(data?.failed) ? data.failed : []).filter((f) => typeof f === 'string' && f);
  const undoId = typeof data?.undo_id === 'string' ? data.undo_id : '';
  const Icon = failed.length > 0 ? AlertTriangle : Check;

  const onUndo = () => {
    if (undoTapped || !undoId) return;
    setUndoTapped(true);
    sendFollowUp?.('undo-archive', 'Undo archive', { mail_action: 'undo_archive', undo_id: undoId });
  };

  return (
    <div style={S.card} data-testid="mail-unsubscribe-result">
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <Icon size={18} color={failed.length > 0 ? C.orange : C.green} strokeWidth={2.5} style={{ flexShrink: 0, marginTop: 1 }} aria-hidden="true" />
        <div style={{ minWidth: 0 }}>
          <p style={S.title}>Unsubscribed {unsubscribed.toLocaleString()} · Archived {archived.toLocaleString()}</p>
          <p style={S.sub}>These senders won't show up in your digest anymore</p>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <StatTile label="Unsubscribed" value={unsubscribed} />
        <StatTile label="Archived" value={archived} />
        <StatTile label="Manual" value={manual.length} />
      </div>
      {manual.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 240, overflowY: 'auto' }}>
          {manual.map((m, i) => <ManualRow key={i} name={m.name} url={m.url} />)}
        </ul>
      )}
      {failed.length > 0 && (
        <p style={{ ...S.warn, ...clampLines(2) }} data-testid="mail-unsubscribe-failed">
          Couldn't unsubscribe from {failed.join(', ')}
        </p>
      )}
      {undoId && (
        <button
          type="button"
          style={btn({ ...S.btnSecondary, flex: 'none', width: '100%' }, undoTapped)}
          disabled={undoTapped}
          onClick={onUndo}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Undo2 size={14} aria-hidden="true" />
            Undo archive
          </span>
        </button>
      )}
    </div>
  );
}

export default function MailResultCard({ data, sendFollowUp }) {
  if (data?.action === 'unsubscribe') return <UnsubscribeResult data={data} sendFollowUp={sendFollowUp} />;
  const partial = data?.status === 'partial' || (Array.isArray(data?.failed) && data.failed.length > 0);
  const Icon = partial ? AlertTriangle : Check;
  return (
    <div style={{ ...S.card, flexDirection: 'row', alignItems: 'center', gap: 8, padding: '10px 14px' }}>
      <Icon size={16} color={partial ? C.orange : C.green} strokeWidth={2.5} style={{ flexShrink: 0 }} aria-hidden="true" />
      <span style={{ ...S.ellipsis, fontSize: 14, fontWeight: 600 }}>{resultText(data)}</span>
    </div>
  );
}
